import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AccountWrapper from './../accountwrapper/AccountWrapper';
import Subscription from './subscription/Subscription';
import InvoicesList from './invoices-list/InvoicesList';
import { getCurrrentPlan } from '../../../helpers/state';
import { friendlyDate } from '../../../helpers/datetime';
import { ACCOUNT_TITLE_BEDROCK } from '../../../constants/Copy';
import { setCustomVariable } from '../../../helpers/usabilla';
import { analyticsPush } from '../../../actions/applicationActions';
import { useQuery } from '@apollo/client';
import { getPaymentClient } from '../../../api/paymentService';
import queryPaymentDetails from '../../../graphql/queryPaymentDetails.graphql';
import useSearchParams from '../../../hooks/useSearchParams';
import { reportCustomError } from '../../../helpers/realUserMonitoring';
import { hasValidVideolandCookie } from '../../../helpers/authentication';
import { redirectToWinbackPage } from '../../../helpers/redirect';
import { ROUTE_TIER_OPTIONS_WINBACK } from '../../../constants/Routes';
import ProductionConfig from '../../../../../config/production';
import { useHistory } from 'react-router';
import { stateRender } from '../../../helpers/react';
import RenderTierInfo from './renderTierInfo/RenderTierInfo';
import BackButton from '../../common/buttons/back/BackButton';
import testConfig from '../../../../../config/test';
import prodConfig from '../../../../../config/production';
import './AccountBedrock.scss';
import { useSubscriptionCancellationStatus } from '../subscription/useSubscriptionCancellationStatus';
import { getSubscriptionClient } from '../../../api/subscriptionService';
import queryTvGemistFallback from '../../../graphql/queryTvGemistFallback.graphql';
import { PAYMENT_ORIGIN } from '../../../constants/Payment';
import Notifications from './notifications/Notifications';
import { useInvoices } from '../../../hooks/useInvoices';
import { useVideolandAccountState } from '../../../hooks/useVideolandAccountState';
import { TIER_IDS } from '../../../constants/Tiers';
import { TierTitles } from '../../../constants/TierTitles';

export default () => {
	const dispatch = useDispatch();
	const isLoggedIn = hasValidVideolandCookie();
	const subscription = useSelector((state) => state.subscription);
	const stitchedSubscription = useSelector((state) => state.subscription.stitchedSubscription.data);

	const isActiveNPLSubscription = useSelector(
		(state) => state.subscription.isActiveNPLSubscription
	);
	const isConvertedToTermedUser = useSelector(
		(state) => state.subscription.isConvertedToTermedUser
	);
	const activeSubscriptionStatus = useSelector((state) => state.subscription.details);
	const activeSubscription = activeSubscriptionStatus?.data;
	const currentPlan = useSelector((state) => getCurrrentPlan(state));
	const {
		openPayments,
		invoices,
		loading: invoicesLoading,
		error: invoicesError,
		refetch: invoicesRefetch,
	} = useInvoices();
	const { priceIncrease } = useVideolandAccountState();

	const { tierSwitch, nextBillingDate } =
		useSelector((state) => state.subscription.tier.data) || {};

	const nextTier = useSelector((state) => state.subscription.tier.data?.nextTier);
	const paymentClient = getPaymentClient();
	const { data: paymentDetails, error: paymentDetailsError } = useQuery(queryPaymentDetails, {
		client: paymentClient,
		skip: !isLoggedIn,
	});
	const { getSearchParam } = useSearchParams();
	const tierChangeEffectiveDate = getSearchParam('tierChangeEffectiveDate');
	const switchedTierFrom = getSearchParam('switchedTierFrom');
	const effectiveDate = tierChangeEffectiveDate && friendlyDate(tierChangeEffectiveDate);
	const history = useHistory();
	const { isCancelled } = useSubscriptionCancellationStatus();
	const subscriptionClient = getSubscriptionClient();
	const { data: tvGemistFallbackData, refetch: refetchTvGemistFallback } = useQuery(
		queryTvGemistFallback,
		{
			client: subscriptionClient,
			skip: !isLoggedIn,
		}
	);

	const isTvGemistFallbackEnabled = tvGemistFallbackData?.me?.isTvGemistFallbackEnabled;

	useEffect(() => {
		setCustomVariable('MoodRatingControl');
	}, [dispatch]);

	useEffect(() => {
		if (paymentDetailsError) {
			reportCustomError({
				name: 'payment_details_error',
				paymentDetailsError,
				message: `is logged in: ${isLoggedIn}, inRequest: ${JSON.stringify(paymentClient)}`,
			});
		}
	}, [paymentDetailsError, isLoggedIn, paymentClient]);

	useEffect(() => {
		if (activeSubscription?.status?.toUpperCase() === 'CANCELLED') {
			if (window.location.href.includes(ProductionConfig.host)) {
				redirectToWinbackPage();
			} else {
				history.push(ROUTE_TIER_OPTIONS_WINBACK);
			}
		}
	}, [activeSubscription, history]);

	useEffect(() => {
		if (switchedTierFrom === TierTitles[TIER_IDS.ESSENTIAL].title) {
			setCustomVariable('switchedTierFromBasis');
		}
		if (switchedTierFrom === TierTitles[TIER_IDS.STANDARD].title) {
			setCustomVariable('switchedTierFromPlus');
		}
	});

	function redirectToBedrock() {
		let bedrockUrl = testConfig.bedrock;
		if (window.location.href.includes(prodConfig.host)) {
			bedrockUrl = prodConfig.bedrock;
		}
		window.location.href = `${bedrockUrl}mijn-account`;
	}

	return (
		<>
			{stateRender({
				isError: paymentDetailsError || activeSubscriptionStatus.error,
				onSuccess: () => (
					<div className="account-page-bedrock-container">
						<Notifications
							showPriceIncrease={
								priceIncrease &&
								currentPlan?.planDescription.tierId !== TIER_IDS.FREE &&
								!activeSubscription.trustedParty
							}
							effectiveDate={effectiveDate}
							openPayments={openPayments}
						/>
						<AccountWrapper showInvoicesFirst={!!paymentDetails?.me?.paymentDetails && isCancelled}>
							<div className="subscription-bedrock-container">
								<BackButton onClick={redirectToBedrock} />
								<p className="subscription-bedrock-title">{ACCOUNT_TITLE_BEDROCK}</p>
							</div>
							{currentPlan && activeSubscription && (
								<>
									<RenderTierInfo
										activeSubscription={activeSubscription}
										isActiveNPLSubscription={isActiveNPLSubscription}
										isConvertedToTermedUser={isConvertedToTermedUser}
										nextBillingDate={nextBillingDate}
										currentPlan={currentPlan}
										isCancelled={isCancelled}
										isLoading={
											activeSubscriptionStatus.pending || activeSubscriptionStatus.pristine
										}
										isStitched={!!stitchedSubscription}
									/>
									<Subscription
										subscription={subscription}
										isConvertedToTermedUser={isConvertedToTermedUser}
										activeSubscription={activeSubscription}
										isActiveNPLSubscription={isActiveNPLSubscription}
										currentPlan={currentPlan}
										analyticsPush={analyticsPush}
										tierSwitch={tierSwitch}
										nextTier={nextTier}
										paymentDetails={paymentDetails}
										isTvGemistFallbackEnabled={isTvGemistFallbackEnabled}
										onRefetchTvGemistFallback={refetchTvGemistFallback}
										isLoading={
											activeSubscriptionStatus.pending || activeSubscriptionStatus.pristine
										}
									/>
								</>
							)}
							<InvoicesList
								isCancelled={isCancelled}
								origin={PAYMENT_ORIGIN.SUBSCRIPTION_MANAGEMENT}
								invoices={invoices}
								isLoading={invoicesLoading}
								error={invoicesError}
								refetch={invoicesRefetch}
							/>
						</AccountWrapper>
					</div>
				),
			})}
		</>
	);
};
